import styled from 'styled-components/macro';
import { KeyboardEvent } from 'react';
import { useIntl } from 'react-intl';

import { lighten, Chip, ChipProps } from '@mui/material';
import { CancelOutlined, ArrowLeft, ArrowRight } from '@mui/icons-material';

export interface HiChipDateProps {
  /**
   * The content of the component.
   */
  label: string;
  /**
   * Callback fired when the remove icon is clicked.
   */
  onAction: ChipProps['onDelete'];
  /**
   * Callback fired when the previous arrow icon is clicked.
   */
  onPrevious?: ChipProps['onDelete'];
  /**
   * Callback fired when the next arrow icon is clicked.
   */
  onNext?: ChipProps['onDelete'];
}

/**
 * The HiChipDate component is used in Console for filters with `date` type.
 *
 * This is not a recommended pattern to have 3 actions in the same chip. This component is temporary.
 */
const HiChipDate = ({
  label,
  onAction,
  onPrevious,
  onNext,
  ...other
}: HiChipDateProps) => {
  const intl = useIntl();

  const handleKeyDown = (event: KeyboardEvent) => {
    // If "space" pressed when Chip is focused, call onAction
    if (event.code === 'Space' && onAction) {
      event.preventDefault();
      onAction(event);
    }
    // If "arrow left" pressed when Chip is focused, call onPrevious
    else if (event.code === 'ArrowLeft' && onPrevious) {
      event.preventDefault();
      onPrevious(event);
    }
    // If "arrow right" pressed when Chip is focused, call onNext
    else if (event.code === 'ArrowRight' && onNext) {
      event.preventDefault();
      onNext(event);
    }
  };

  return (
    <StyledChip
      {...other}
      classes={{
        deleteIcon: 'HiChipDate-actionIcon'
      }}
      label={
        <span className="HiChipDate-label-container">
          {onPrevious && (
            <ArrowLeft
              className="HiChipDate-actionIcon HiChipDate-arrowLeft"
              onClick={onPrevious}
            ></ArrowLeft>
          )}
          {label}
          {onNext && (
            <ArrowRight
              className="HiChipDate-actionIcon"
              onClick={onNext}
            ></ArrowRight>
          )}
        </span>
      }
      size="small"
      variant="filled"
      onDelete={onAction}
      deleteIcon={
        <CancelOutlined
          titleAccess={intl.formatMessage({ id: 'HiChip.remove' })}
        />
      }
      onKeyDown={handleKeyDown}
    />
  );
};

const StyledChip = styled(Chip).attrs(({ color = 'info' }) => ({
  color: color
}))`
  color: ${({ theme }) => theme.palette.info.dark};
  background-color: ${({ theme }) => lighten(theme.palette.info.main, 0.9)};

  &:focus-visible {
    background-color: ${({ theme }) => lighten(theme.palette.info.main, 0.7)};
  }

  .HiChipDate-actionIcon {
    cursor: pointer;
    color: ${({ theme }) => theme.palette.info.main};

    &:hover {
      color: ${({ theme }) => theme.palette.info.dark};

      transform: scale(1.2);
      transition: ease-in 0.1s;
    }

    &.HiChipDate-arrowLeft {
      margin-left: 0;
    }
  }

  .HiChipDate-label-container {
    display: flex;
    align-items: center;
  }
`;

export default HiChipDate;
